import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { documentService, type Document } from '../../services/documentService';
import type { AppSearchResult } from './appSearchTypes';

/**
 * #1392 — app-wide search result composition.
 *
 * Two sources, deliberately decoupled:
 *  - gutschrift-check match groups already in memory (all loaded months),
 *    passed in by the controller as a flat ``GsSearchGroup[]`` so this
 *    module never imports the chokepoint canvas files.
 *  - the server-side ``/documents`` search (tenant-wide), debounced.
 */

const DEFAULT_GS_LIMIT = 8;
const DEFAULT_DOC_LIMIT = 8;
const DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

/**
 * Flattened, search-ready projection of a gutschrift-check match group.
 * The controller derives this from ``useMatchGroups`` output; keeping it
 * a plain data shape (no MatchGroup import) means this module and its
 * tests carry no dependency on the GS-Check canvas internals.
 */
export interface GsSearchGroup {
  id: string;
  /** Period (YYYY-MM) the group belongs to — drives month switch + badge. */
  ym: string;
  status: string;
  deliveryProductNames: string[];
  settlementProductNames: string[];
  agreementNumbers: string[];
  partnerNames: string[];
  documentNumbers: string[];
  money: { delta: number; currency: string } | null;
}

function haystack(group: GsSearchGroup): string {
  return [
    group.id,
    ...group.deliveryProductNames,
    ...group.settlementProductNames,
    ...group.agreementNumbers,
    ...group.partnerNames,
    ...group.documentNumbers,
  ]
    .join(' ')
    .toLowerCase();
}

function gsBadge(group: GsSearchGroup): string {
  return `GS-Check · ${group.ym}`;
}

function gsSublabel(group: GsSearchGroup): string | undefined {
  const product =
    group.deliveryProductNames[0] ?? group.settlementProductNames[0];
  const partner = group.partnerNames[0];
  return [product, partner].filter(Boolean).join(' · ') || undefined;
}

/** Build app-search results from already-loaded GS match groups. */
export function buildGsGroupResults(
  query: string,
  groups: GsSearchGroup[],
  options: { limit?: number } = {},
): AppSearchResult[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const limit = options.limit ?? DEFAULT_GS_LIMIT;
  const results: AppSearchResult[] = [];
  for (const group of groups) {
    if (!haystack(group).includes(needle)) continue;
    results.push({
      id: `gs:${group.id}`,
      kind: 'gs-group',
      label: group.agreementNumbers[0] || group.documentNumbers[0] || group.id,
      sublabel: gsSublabel(group),
      badge: gsBadge(group),
      jump: { type: 'gs-node', groupId: group.id, ym: group.ym },
    });
    if (results.length >= limit) break;
  }
  return results;
}

/** Map server-side document hits to app-search results. */
export function buildDocumentResults(documents: Document[]): AppSearchResult[] {
  return documents.map((d) => ({
    id: `doc:${d.doc_id}`,
    kind: 'document' as const,
    label: d.smart_filename || d.filename,
    // #1930 B2a: the taxonomy-v2 role label wins; the legacy display name /
    // raw document_type stay as fallbacks for NULL-cell rows.
    sublabel:
      [d.role_name || d.document_type_display_name || d.document_type, d.document_date]
        .filter(Boolean)
        .join(' · ') || undefined,
    badge: d.document_date ? `Dokument · ${d.document_date}` : 'Dokument',
    jump: { type: 'route', to: `/documents?doc=${encodeURIComponent(d.doc_id)}` },
  }));
}

/** Internal: debounce a string value. */
function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export interface UseAppSearchResultsArgs {
  /** The active search query (from SearchContext). */
  query: string;
  /** Flattened GS-Check groups in memory (empty off the GS-Check page). */
  gsGroups: GsSearchGroup[];
  /** Gate the whole hook (e.g. only when the search box has focus). */
  enabled?: boolean;
}

export interface UseAppSearchResultsValue {
  results: AppSearchResult[];
  /** True while the server-side document query is in flight. */
  isLoading: boolean;
}

/**
 * Compose tenant-wide results: in-memory GS groups (synchronous) + the
 * debounced server-side ``/documents`` search. GS hits sort first so the
 * in-app jump is the primary affordance; documents (with the /documents
 * fallback jump) follow.
 */
export function useAppSearchResults({
  query,
  gsGroups,
  enabled = true,
}: UseAppSearchResultsArgs): UseAppSearchResultsValue {
  const debouncedQuery = useDebounced(query.trim(), DEBOUNCE_MS);
  const active = enabled && debouncedQuery.length >= MIN_QUERY_LEN;

  const docQuery = useQuery({
    queryKey: ['app-search', 'documents', debouncedQuery],
    queryFn: () =>
      documentService.listDocuments({
        search: debouncedQuery,
        limit: DEFAULT_DOC_LIMIT,
        offset: 0,
      }),
    enabled: active,
    staleTime: 30_000,
  });

  const gsResults = useMemo(
    () => (active ? buildGsGroupResults(debouncedQuery, gsGroups) : []),
    [active, debouncedQuery, gsGroups],
  );

  const docResults = useMemo(
    () => (docQuery.data ? buildDocumentResults(docQuery.data.documents) : []),
    [docQuery.data],
  );

  const results = useMemo(() => {
    if (!active) return [];
    return [...gsResults, ...docResults];
  }, [active, gsResults, docResults]);

  return { results, isLoading: active && docQuery.isFetching };
}
